/**
 * Shared absence date-picker rules.
 *
 * Absence type `code` may be a string name (`sick_leave`) or the Rails enum
 * integer (`5`). Sick leave and compensatory time off have no day cap.
 * Only sick leave may span public holidays (røde dager).
 */

const UNLIMITED_DURATION_CODES = new Set([
  "sick_leave",
  "compensatory_time_off",
  4,
  5,
  "4",
  "5",
]);

const SICK_LEAVE_CODES = new Set(["sick_leave", 5, "5"]);

export function absenceTypeCode(typeOrCode) {
  if (typeOrCode == null) return null;
  if (typeof typeOrCode === "object") return typeOrCode.code ?? null;
  return typeOrCode;
}

export function isUnlimitedAbsenceDuration(typeOrCode) {
  const code = absenceTypeCode(typeOrCode);
  return code != null && UNLIMITED_DURATION_CODES.has(code);
}

export function isSickLeaveAbsenceType(typeOrCode) {
  const code = absenceTypeCode(typeOrCode);
  return code != null && SICK_LEAVE_CODES.has(code);
}

export function absenceMaySpanPublicHolidays(typeOrCode) {
  return isSickLeaveAbsenceType(typeOrCode);
}

/** Calendar-day cap for PMDatePicker. Undefined = no frontend max. */
export function absencePickerMaxDays(typeOrCode) {
  if (isUnlimitedAbsenceDuration(typeOrCode)) return undefined;
  if (!typeOrCode || typeof typeOrCode !== "object") return undefined;
  const raw = typeOrCode.max_duration_days;
  if (raw == null || raw === "") return undefined;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

/**
 * End-date validity for the absence "Dato til" picker.
 * Sick leave always returns true so røde dager never disable later days.
 */
export function isAbsenceEndDateValid({
  dateStr,
  dateFrom,
  absenceType,
  isPublicHolidayDate,
  rangeIncludesPublicHoliday,
  userId,
} = {}) {
  if (isSickLeaveAbsenceType(absenceType)) {
    return true;
  }

  if (typeof isPublicHolidayDate === "function" && isPublicHolidayDate(dateStr)) {
    return false;
  }

  if (
    dateFrom &&
    typeof rangeIncludesPublicHoliday === "function" &&
    rangeIncludesPublicHoliday(dateFrom, dateStr)
  ) {
    return false;
  }

  if (isUnlimitedAbsenceDuration(absenceType)) {
    return true;
  }

  const maxDays = absencePickerMaxDays(absenceType);
  if (!dateFrom || !maxDays) {
    return true;
  }

  // Backend validates working days when a user is selected.
  if (userId) {
    return true;
  }

  const start = new Date(dateFrom);
  const end = new Date(dateStr);
  const diffTime = Math.abs(end - start);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  return diffDays <= maxDays;
}
